import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faGear, faCalendarDays, faPlus, faBan,
  faCheckCircle, faTimesCircle, faClock, faSave, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-500', error: 'bg-red-500', info: 'bg-blue-500' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="ml-2 font-bold">✕</button>
    </div>
  );
};

/* ── helpers ── */
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.substring(0, 5).split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  const s = typeof d === 'string' && d.includes('T') ? d.split('T')[0] : String(d);
  const [y, mo, day] = s.split('-');
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${parseInt(day)} ${months[parseInt(mo) - 1]} ${y}`;
};

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const nowTimeStr = () => {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
};

const AdminSalon = () => {
  const [tab, setTab] = useState('reservas');
  const [config, setConfig] = useState(null);
  const [editConfig, setEditConfig] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [towers, setTowers] = useState([]);
  const [torreApartments, setTorreApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ msg: '', type: 'success' });
  const [savingConfig, setSavingConfig] = useState(false);

  // Form nueva reserva
  const [showForm, setShowForm] = useState(false);
  const [formTorre, setFormTorre] = useState('');
  const [formData, setFormData] = useState({
    fecha: '', hora_inicio: '', hora_fin: '', motivo: '', observacion: '',
    id_apartamento: '', id_usuario: ''
  });
  const [aptoUsers, setAptoUsers] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [conflictos, setConflictos] = useState([]);
  const [aptMora, setAptMora] = useState(false);
  const [moraMensaje, setMoraMensaje] = useState('');

  const showMsg = (msg, type = 'success') => setSnack({ msg, type });
  const fmt = (t) => t ? t.substring(0, 5) : '';

  const fetchConfig = useCallback(async () => {
    const r = await axios.get(`${BASE_URL}/amenities/salon/config`);
    setConfig(r.data);
    setEditConfig({ ...r.data });
  }, []);

  const fetchReservations = useCallback(async (fecha) => {
    const params = fecha ? { fecha } : {};
    const r = await axios.get(`${BASE_URL}/amenities/salon/reservations`, { params });
    setReservations(r.data);
  }, []);

  const fetchTowers = useCallback(async () => {
    const r = await axios.get(`${BASE_URL}/towers`);
    setTowers(r.data);
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchReservations(''), fetchTowers()])
      .finally(() => setLoading(false));
  }, [fetchConfig, fetchReservations, fetchTowers]);

  // Cargar conflictos cuando cambia la fecha
  useEffect(() => {
    if (!formData.fecha) { setConflictos([]); return; }
    axios.get(`${BASE_URL}/amenities/salon/availability`, { params: { fecha: formData.fecha } })
      .then(r => setConflictos(r.data.reservadas || []))
      .catch(() => setConflictos([]));
  }, [formData.fecha]);

  // Cargar apartamentos cuando cambia la torre
  useEffect(() => {
    if (!formTorre) { setTorreApartments([]); setFormData(p => ({ ...p, id_apartamento: '', id_usuario: '' })); return; }
    axios.get(`${BASE_URL}/getApartmentsData/${formTorre}`)
      .then(r => setTorreApartments(r.data))
      .catch(() => setTorreApartments([]));
    setFormData(p => ({ ...p, id_apartamento: '', id_usuario: '' }));
    setAptMora(false); setMoraMensaje('');
  }, [formTorre]);

  // Cargar usuarios por apartamento
  useEffect(() => {
    if (!formData.id_apartamento) { setAptoUsers([]); setFormData(p => ({ ...p, id_usuario: '' })); setAptMora(false); setMoraMensaje(''); return; }
    const apto = torreApartments.find(a => String(a.id_apartamento) === String(formData.id_apartamento));
    if (apto && apto.estado_cartera === 'en_mora') {
      setAptMora(true);
      setMoraMensaje(`El apartamento ${apto.num_apartamento} tiene pagos pendientes con administración. No se puede realizar esta reserva.`);
    } else {
      setAptMora(false);
      setMoraMensaje('');
    }
    axios.get(`${BASE_URL}/users/apartment/${formData.id_apartamento}`)
      .then(r => {
        setAptoUsers(r.data);
        if (r.data.length > 0)
          setFormData(p => ({ ...p, id_usuario: String(r.data[0].id_usuario) }));
      })
      .catch(() => setAptoUsers([]));
  }, [formData.id_apartamento, torreApartments]);

  const handleFilterDate = (d) => {
    setFilterDate(d);
    fetchReservations(d);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    try {
      await axios.put(`${BASE_URL}/amenities/salon/reservations/${id}/cancel`);
      showMsg('Reserva cancelada');
      fetchReservations(filterDate);
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al cancelar', 'error');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const { fecha, hora_inicio, hora_fin, id_apartamento, id_usuario, motivo, observacion } = formData;
    if (!fecha || !hora_inicio || !hora_fin || !id_apartamento || !id_usuario) {
      showMsg('Completa todos los campos requeridos', 'error'); return;
    }
    if (aptMora) { showMsg(moraMensaje, 'error'); return; }
    if (fecha < todayStr()) {
      showMsg('No se puede reservar en una fecha pasada', 'error'); return;
    }
    if (fecha === todayStr() && hora_inicio < nowTimeStr()) {
      showMsg('La hora de inicio no puede ser anterior a la hora actual', 'error'); return;
    }
    if (hora_inicio >= hora_fin) {
      showMsg('La hora de inicio debe ser antes de la hora de fin', 'error'); return;
    }
    if (!config) { showMsg('Error de configuración', 'error'); return; }
    if (hora_inicio < config.hora_inicio.substring(0, 5) || hora_fin > config.hora_fin.substring(0, 5)) {
      showMsg(`El salón solo está disponible de ${fmtTime(config.hora_inicio)} a ${fmtTime(config.hora_fin)}`, 'error');
      return;
    }
    try {
      await axios.post(`${BASE_URL}/amenities/salon/reservations`, {
        id_apartamento, id_usuario, fecha,
        hora_inicio: hora_inicio + ':00',
        hora_fin: hora_fin + ':00',
        motivo, observacion
      });
      showMsg('Reserva de salón creada exitosamente');
      setShowForm(false);
      setFormTorre('');
      setFormData({ fecha: '', hora_inicio: '', hora_fin: '', motivo: '', observacion: '', id_apartamento: '', id_usuario: '' });
      fetchReservations(filterDate);
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al crear reserva', 'error');
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${BASE_URL}/amenities/salon/config`, editConfig);
      showMsg('Configuración guardada');
      fetchConfig();
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al guardar', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) return (
    <>
      <Navigation />
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-purple-600"></div>
      </div>
    </>
  );

  return (
    <>
      <Navigation />
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow">
              <FontAwesomeIcon icon={faBuilding} className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Salón Comunal — Gestión de Reservas</h1>
              {config && (
                <p className="text-sm text-gray-500 mt-1">
                  Disponible: {fmtTime(config.hora_inicio)} – {fmtTime(config.hora_fin)} ·
                  {config.activo ? ' Activo' : ' Inactivo'}
                </p>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[{ id: 'reservas', label: 'Reservas', icon: faCalendarDays },
              { id: 'config', label: 'Configuración', icon: faGear }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all
                  ${tab === t.id ? 'bg-purple-600 text-white shadow' : 'bg-white text-gray-600 hover:bg-purple-50'}`}>
                <FontAwesomeIcon icon={t.icon} />
                {t.label}
              </button>
            ))}
          </div>

          {/* ── RESERVAS ───────────────────────────────────── */}
          {tab === 'reservas' && (
            <>
              <div className="bg-white rounded-2xl shadow p-4 mb-4 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCalendarDays} className="text-purple-500" />
                  <label className="text-sm font-medium text-gray-700">Filtrar por fecha:</label>
                  <input type="date" value={filterDate} onChange={e => handleFilterDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                  {filterDate && (
                    <button onClick={() => handleFilterDate('')}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors">Limpiar</button>
                  )}
                </div>
                <button onClick={() => setShowForm(!showForm)}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow">
                  <FontAwesomeIcon icon={faPlus} />
                  Nueva reserva
                </button>
              </div>

              {/* Formulario */}
              {showForm && (
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 mb-4">
                  <h3 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlus} />
                    Nueva reserva de salón comunal
                  </h3>

                  {/* Ocupaciones del día seleccionado */}
                  {formData.fecha && conflictos.length > 0 && (
                    <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-yellow-800 mb-2">Horarios ya reservados en esta fecha:</p>
                      <div className="flex flex-wrap gap-2">
                        {conflictos.map((c, i) => (
                          <span key={i} className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded-lg text-xs font-medium">
                              {fmtTime(c.hora_inicio)} – {fmtTime(c.hora_fin)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                      <input type="date" value={formData.fecha}
                        onChange={e => setFormData(p => ({ ...p, fecha: e.target.value }))} required
                        min={todayStr()}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                      <input type="text" value={formData.motivo}
                        onChange={e => setFormData(p => ({ ...p, motivo: e.target.value }))}
                        placeholder="ej: Cumpleaños, Reunión..." maxLength={255}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hora inicio *</label>
                      <input type="time" value={formData.hora_inicio}
                        onChange={e => setFormData(p => ({ ...p, hora_inicio: e.target.value }))} required
                        min={(() => {
                          const cfgMin = config ? config.hora_inicio.substring(0, 5) : '07:00';
                          if (formData.fecha === todayStr()) {
                            const now = nowTimeStr();
                            return now > cfgMin ? now : cfgMin;
                          }
                          return cfgMin;
                        })()}
                        max={config ? config.hora_fin.substring(0, 5) : '22:00'}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hora fin *</label>
                      <input type="time" value={formData.hora_fin}
                        onChange={e => setFormData(p => ({ ...p, hora_fin: e.target.value }))} required
                        min={formData.hora_inicio || (config ? config.hora_inicio.substring(0, 5) : '07:00')}
                        max={config ? config.hora_fin.substring(0, 5) : '22:00'}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FontAwesomeIcon icon={faBuilding} className="mr-1 text-purple-400" />Torre *
                      </label>
                      <select value={formTorre} onChange={e => setFormTorre(e.target.value)} required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none">
                        <option value="">Selecciona una torre</option>
                        {towers.map(t => (
                          <option key={t.id_torre} value={t.id_torre}>{t.nombre}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Apartamento *</label>
                      <select value={formData.id_apartamento}
                        onChange={e => setFormData(p => ({ ...p, id_apartamento: e.target.value, id_usuario: '' }))} required
                        disabled={!formTorre}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400">
                        <option value="">{formTorre ? 'Selecciona un apartamento' : 'Selecciona torre primero'}</option>
                        {torreApartments.map(a => (
                          <option key={a.id_apartamento} value={a.id_apartamento}>Apto {a.num_apartamento}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Inquilino *</label>
                      <select value={formData.id_usuario}
                        onChange={e => setFormData(p => ({ ...p, id_usuario: e.target.value }))} required
                        disabled={!formData.id_apartamento || aptoUsers.length === 0}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none">
                        <option value="">{formData.id_apartamento ? (aptoUsers.length ? 'Selecciona' : 'Sin inquilinos') : 'Selecciona apartamento primero'}</option>
                        {aptoUsers.map(u => (
                          <option key={u.id_usuario} value={u.id_usuario}>{u.nombre} {u.apellido}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
                      <input type="text" value={formData.observacion}
                        onChange={e => setFormData(p => ({ ...p, observacion: e.target.value }))}
                        placeholder="Opcional" maxLength={255}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                    </div>

                    {aptMora && (
                      <div className="md:col-span-2 flex items-start gap-2 bg-red-50 border border-red-300 rounded-lg px-4 py-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold text-red-800">Reserva no permitida</p>
                          <p className="text-sm text-red-700 mt-0.5">{moraMensaje}</p>
                        </div>
                      </div>
                    )}
                    <div className="md:col-span-2 flex gap-3 justify-end">
                      <button type="button" onClick={() => { setShowForm(false); setFormTorre(''); }}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-red-500 bg-gray-100 rounded-lg transition-colors">
                        Cancelar
                      </button>
                      <button type="submit"
                        disabled={aptMora}
                        className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors shadow ${aptMora ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white'}`}>
                        Crear reserva
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Tabla */}
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                {reservations.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <FontAwesomeIcon icon={faCalendarDays} className="h-12 w-12 mb-3 opacity-30" />
                    <p className="text-lg font-medium">No hay reservas{filterDate ? ` para ${fmtDate(filterDate)}` : ''}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          {['Fecha', 'Horario', 'Torre', 'Apto', 'Inquilino', 'Teléfono', 'Motivo', 'Estado', 'Acción'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {reservations.map(r => (
                          <tr key={r.id_reserva} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 font-medium">{fmtDate(r.fecha)}</td>
                            <td className="px-4 py-3">
                              <span className="flex items-center gap-1 text-purple-700">
                                <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5" />
                                {fmtTime(r.hora_inicio)} – {fmtTime(r.hora_fin)}
                              </span>
                            </td>
                            <td className="px-4 py-3">{r.torre_nombre}</td>
                            <td className="px-4 py-3 font-semibold">{r.num_apartamento}</td>
                            <td className="px-4 py-3">{r.inquilino_nombre} {r.inquilino_apellido}</td>
                            <td className="px-4 py-3 text-gray-500">{r.inquilino_telefono}</td>
                            <td className="px-4 py-3 text-gray-500">{r.motivo || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
                                ${r.estado === 'confirmada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                <FontAwesomeIcon icon={r.estado === 'confirmada' ? faCheckCircle : faTimesCircle} className="h-3 w-3" />
                                {r.estado}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {r.estado === 'confirmada' && (
                                <button onClick={() => handleCancel(r.id_reserva)}
                                  className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors border border-red-200">
                                  <FontAwesomeIcon icon={faBan} />
                                  Cancelar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── CONFIGURACIÓN ───────────────────────────────── */}
          {tab === 'config' && editConfig && (
            <div className="bg-white rounded-2xl shadow p-6 max-w-xl">
              <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                <FontAwesomeIcon icon={faGear} className="text-purple-500" />
                Configuración del salón comunal
              </h3>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora de apertura</label>
                    <input type="time" value={editConfig.hora_inicio ? editConfig.hora_inicio.substring(0, 5) : ''}
                      onChange={e => setEditConfig(p => ({ ...p, hora_inicio: e.target.value + ':00' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora de cierre</label>
                    <input type="time" value={editConfig.hora_fin ? editConfig.hora_fin.substring(0, 5) : ''}
                      onChange={e => setEditConfig(p => ({ ...p, hora_fin: e.target.value + ':00' }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 focus:outline-none" />
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <input type="checkbox" id="salonActivo" checked={!!editConfig.activo}
                      onChange={e => setEditConfig(p => ({ ...p, activo: e.target.checked ? 1 : 0 }))}
                      className="w-4 h-4 text-purple-600 rounded" />
                    <label htmlFor="salonActivo" className="text-sm font-medium text-gray-700">
                      Salón activo (recibe reservas)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button onClick={handleSaveConfig} disabled={savingConfig}
                    className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow">
                    <FontAwesomeIcon icon={faSave} />
                    {savingConfig ? 'Guardando...' : 'Guardar configuración'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default AdminSalon;
